import React from "react";
import { Check, Target, Eye, Award } from "lucide-react";
import { images, aboutValues, stats, company } from "../mock/mock";
import { PageHero, SectionHeading, PrimaryButton, CTABanner } from "../components/Common";

const About = () => {
  return (
    <div>
      <PageHero
        eyebrow="About Us"
        title="Your extended team for contact centre excellence"
        subtitle="Since 2018, Mid Atlantic Business Management has helped businesses across the USA and Canada lower labor costs, improve customer experience and grow revenue through world-class outsourcing."
        image={images.corporateTeam}
      />

      {/* Story */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <div className="img-zoom rounded-2xl shadow-xl">
            <img src={images.handshake} alt="Our team" className="w-full h-[440px] object-cover rounded-2xl" />
          </div>
          <div>
            <SectionHeading eyebrow="Our Story" title="Built to conquer the objections to outsourcing" />
            <div className="-mt-6 space-y-4 text-muted-foreground leading-relaxed">
              <p>
                Many companies avoid outsource staffing fearing lack of control, communication issues,
                and problems with quality. We intentionally built Mid Atlantic Business Management to
                conquer these and other objections — with transparent reporting, dedicated teams and
                rigorous quality assurance.
              </p>
              <p>
                Operating from North Carolina and our operations center in Pune, India, we combine
                skilled, experienced agents with modern tools to deliver affordable, scalable and
                virtual staffing. Insurers, hospitals, law firms and B2B companies trust us to run the
                programs that keep their businesses moving.
              </p>
              <p>
                Give us 30 days and we'll open your eyes to lower labor costs and greater revenue —
                backed by our money-back guarantee.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats band */}
      <section className="brand-gradient py-14">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
          {stats.map((s) => (
            <div key={s.label}>
              <div className="font-heading font-extrabold text-white text-4xl">
                {s.value}<span className="text-gold">{s.suffix}</span>
              </div>
              <div className="mt-2 text-sm text-white/70">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Mission / Vision */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-6">
          {[
            { Icon: Target, title: "Our Mission", desc: "To deliver affordable, high-quality outsourcing that helps our clients lower costs, delight customers and grow revenue." },
            { Icon: Eye, title: "Our Vision", desc: "To be the most trusted contact centre and BPO partner for healthcare, insurance and B2B businesses in North America." },
            { Icon: Award, title: "Our Promise", desc: "A true extension of your team, aligned to your goals, with a 30-day money-back guarantee on every engagement." },
          ].map(({ Icon, title, desc }) => (
            <div key={title} className="rounded-2xl border border-border p-8 card-lift">
              <div className="w-12 h-12 rounded-xl brand-gradient flex items-center justify-center mb-5">
                <Icon size={22} className="text-gold-light" />
              </div>
              <h3 className="font-heading font-bold text-navy text-lg mb-2">{title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="bg-secondary py-20">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading center eyebrow="Our Values" title="What guides everything we do" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {aboutValues.map((v) => (
              <div key={v.title} className="bg-white rounded-2xl p-7 border border-border text-center card-lift">
                <div className="w-11 h-11 rounded-full bg-gold flex items-center justify-center mx-auto mb-4">
                  <Check size={20} className="text-navy-dark" />
                </div>
                <h3 className="font-heading font-bold text-navy mb-2">{v.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABanner title="Let's build your success story" />
    </div>
  );
};

export default About;
